import { useState, useCallback } from 'react';
import { trackCustomEvent } from 'utilities/app-insights';

/**
 * Reports Schedule Pagination and Search Hook
 *
 * Manages pagination, search, and sorting state and handlers.
 * Handles:
 * - Pagination state (pageNumber, pageSize, offset)
 * - Search term state and handler with GTM tracking
 * - Sort column and direction state and handler
 * - Sort column name mapping (Schedule -> ScheduleFrequency, etc.)
 *
 * @param params Hook dependencies
 * @returns Pagination/search/sort state and handlers
 */
export const useReportsSchedulePagination = ({
  loggedInUser,
  assignedAccountNumber,
  selectedContractNumber,
}: {
  loggedInUser: any;
  assignedAccountNumber: string;
  selectedContractNumber: string | null;
}) => {
  const [searchTerm, setSearchTerm] = useState('');
  const [sortColumn, setSortColumn] = useState('reportName');
  const [sortDirection, setSortDirection] = useState('asc');
  const [pageNumber, setPageNumber] = useState(1);

  const pageSize = 10;
  const offset = (pageNumber - 1) * pageSize;

  const getSortColumnValue = (input: string): string => {
    if (input === 'Schedule') {
      return 'ScheduleFrequency';
    }

    if (input === 'Output Type') {
      return 'OutputFormat';
    }

    return input;
  };

  const handleSearchTermChange = useCallback(
    (input: string) => {
      window.dataLayer.push({
        event: 'Card_Portal_Reports_Reports_Schedule_Search',
        search_input: input,
      });
      trackCustomEvent(
        'Search Report Schedules',
        loggedInUser,
        assignedAccountNumber,
        'Searched report name to filter schedules.',
        { selectedAccount: selectedContractNumber }
      );
      setPageNumber(1);
      setSearchTerm(input);
    },
    [loggedInUser, assignedAccountNumber, selectedContractNumber]
  );

  const handleTableColumnSorted = useCallback((column: string, isAscending: boolean) => {
    const sortColumnValue = getSortColumnValue(column);
    setSortColumn(sortColumnValue);
    setSortDirection(isAscending ? 'asc' : 'desc');
  }, []);

  const handlePageNumberChange = useCallback(
    (value: number) => {
      setPageNumber(value);

      trackCustomEvent(
        'Change Page Number',
        loggedInUser,
        assignedAccountNumber,
        'Clicked to change page of reports schedule list.'
      );
    },
    [loggedInUser, assignedAccountNumber]
  );

  return {
    searchTerm,
    sortColumn,
    sortDirection,
    pageNumber,
    pageSize,
    offset,
    setSearchTerm,
    setSortColumn,
    setSortDirection,
    setPageNumber,
    handleSearchTermChange,
    handleTableColumnSorted,
    handlePageNumberChange,
  };
};
